/**
 * @author        david.loehr@zumper.com (David Loehr)
 * @copyright     Copyright (c) 2024, Zumper
 */

import type { RouteComponentProps } from '@zcode/router-utils'
import type { Empty } from '@zcode/utility-types'
import hoistNonReactStatics from 'hoist-non-react-statics'
import React from 'react'
import type { GetProps, InferableComponentEnhancerWithProps } from 'react-redux'

/**
 * Every top-level route component (e.g. `FullDetailViewContainer`) has routeProps passed to it,
 * even though they may not use those props.
 *
 * This HOC removes the `history`, `location`, and `match` props, so that the component won't be
 * needlessly re-rendered when those props change
 */
// @ts-expect-error
export const withoutRouteProps: InferableComponentEnhancerWithProps<
  Empty<RouteComponentProps>,
  RouteComponentProps
> = (OriginalComponent) => {
  const Memoed = React.memo(OriginalComponent)
  function WithoutRouteProps({
    history,
    location,
    match,
    ...rest
  }: GetProps<typeof OriginalComponent> & RouteComponentProps) {
    // @ts-expect-error
    return <Memoed {...rest} />
  }
  hoistNonReactStatics(WithoutRouteProps, OriginalComponent)
  return WithoutRouteProps
}
